import { useEffect, useRef, useState } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Header from './components/header'
import SidebarLeft from './components/sidebar/left'
import Home from './pages/home'
import Docs from './pages/docs'
import { ThemeProvider } from 'styled-components'
import { ThemeStyled } from './assets/styles/themeStyled'
import { ThemeProvider as MaterialProvider } from '@mui/material/styles'
import ThemeMaterial from './assets/styles/themeMaterial'
import { AppContainer, Main, StyledContainer } from './assets/styles/styles'
import SidebarRight from './components/sidebar/right'
import useMediaResponsive from './assets/styles/mediaQuery'
import { fetchData } from './sdk'
import stores from './stores/store'
import ButtonContained from './components/buttons/buttonContained'
import { StyledButtonCampaign } from './components/header/styles'
import Thanks from './pages/thanks'

type GroupMapping = {
  [key: string]: string
}

export default function App() {
  const { setLoadingList, setLoadingDescription } = stores.DataLoadingStore()
  const { contentRequested, setContentRequested } =
    stores.DataContentRequestedStore()

  const { setNavbar, setListNavbar } = stores.DataNavbarStore()
  const { listSidebar, setListSidebar } = stores.DataSidebarStore()
  const { setContent } = stores.DataContentStore()
  const { localeSelected, jsonSelected: lang } = stores.DataLocaleStore()
  const { minMedia1250, maxMedia1250 } = useMediaResponsive()
  const location = useLocation()
  const prevLocation = useRef(location.pathname)
  const [langChanged, setLangChanged] = useState(false)
  const [isCampaignMode, setIsCampaignMode] = useState(false)

  useEffect(() => {
    const script = document.createElement('script')
    script.async = true
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-MR53L2CH');
    `
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  function fetchList() {
    setLoadingList(true)

    let groupMapping: GroupMapping

    const groupMappingPt: GroupMapping = {
      'Análise Cadastral': 'Análise Cadastral',
      'Análise de Risco': 'Análise de Risco',
      'Verificação de Crédito': 'Verificação de Crédito',
      'Situação Veicular': 'Situação Veicular',
      'SERASA - Infobusca': 'Infobusca',
      'Serviços cartoriais': 'Serviços cartoriais'
    }

    const groupMappingEn: GroupMapping = {
      'Análise Cadastral': 'Registration Analysis',
      'Análise de Risco': 'Risk Analysis',
      'Verificação de Crédito': 'Credit Check',
      'Situação Veicular': 'Vehicle Status',
      'SERASA - Infobusca': 'Infosearch',
      'Serviços cartoriais': 'Notary Services'
    }

    if (localeSelected === 'PT') {
      groupMapping = groupMappingPt
    } else {
      groupMapping = groupMappingEn
    }

    let url = 'sandbox/v1/listagem_descricao_api'

    if (localeSelected === 'EN') {
      url += '/INGLESA'
    }

    fetchData(url)
      .then((data) => {
        if (data.code === '000') {
          const groups = (
            Object.keys(data.content) as Array<keyof typeof groupMapping>
          ).map((key) => groupMapping[key] || key) as string[]

          setNavbar(groups[0])
          setListNavbar(groups)

          const groupBodies = Object.keys(data.content).map((key) => ({
            name: groupMapping[key as keyof typeof groupMapping],
            list: data.content[key]
              .map((item: any) => ({
                name: item[0],
                href: `/${item[1]}`,
                path: `/${item[2]}`
              }))
              .sort((a: any, b: any) => {
                const numA = parseInt(a.href.match(/\d+/)?.[0] || '0', 10)
                const numB = parseInt(b.href.match(/\d+/)?.[0] || '0', 10)
                return numA - numB
              })
          }))

          setListSidebar(groupBodies)

          setLoadingList(false)
        } else {
          setLoadingList(false)
        }
      })
      .catch((error) => {
        console.error('Fetch error:', error)
        setLoadingList(false)
      })
  }

  function fetchDescription(path: string) {
    setLoadingDescription(true)

    let url = 'sandbox/v1/listagem_descricao_json' + path

    if (localeSelected === 'EN') {
      url += '/INGLESA'
    }

    /* armazeno e valido se esse conteúdo já foi requisitado,
    se nao eu armazeno no success do fetchData e na proxima
    chamada do path eu pego do contentRequested */
    const haveContent =
      Array.isArray(contentRequested) &&
      contentRequested.find((item: any) => item.path === path)

    if (haveContent) {
      const content = contentRequested.filter((item: any) => item.path === path)

      setContent(content[0])
      setLoadingDescription(false)
      return
    }

    fetchData(url)
      .then((data) => {
        if (data.code === '000') {
          data.content.path = path
          setContentRequested(data.content)

          setContent(data.content)
          setLoadingDescription(false)
        } else {
          setContent({})
          setLoadingDescription(false)
        }
      })
      .catch((error) => {
        console.error('Fetch error:', error)
        setLoadingDescription(false)
      })
  }

  useEffect(() => {
    if (location.pathname !== '/' && listSidebar.length > 0) {
      const foundItem = listSidebar
        .flatMap((item) => item.list)
        .find((subItem) => subItem.path === location.pathname)

      if (foundItem?.href) {
        fetchDescription(foundItem.href)
      }
    }
  }, [listSidebar])

  useEffect(() => {
    if (prevLocation.current !== location.pathname) {
      const foundItem = listSidebar
        .flatMap((item) => item.list)
        .find((subItem) => subItem.path === location.pathname)

      if (foundItem?.href) {
        fetchDescription(foundItem.href)
        prevLocation.current = location.pathname
      }
    }
  }, [location])

  useEffect(() => {
    if (langChanged) {
      fetchList()

      if (location.pathname !== '/') {
        const foundItem = listSidebar
          .flatMap((item) => item.list)
          .find((subItem) => subItem.path === location.pathname)

        if (foundItem?.href) {
          fetchDescription(foundItem.href)
        }
      }
    } else {
      setLangChanged(true)
    }
  }, [lang])

  useEffect(() => {
    fetchList()

    if (location.pathname !== '/') {
      const foundItem = listSidebar
        .flatMap((item) => item.list)
        .find((subItem) => subItem.path === location.pathname)

      if (foundItem?.href) {
        fetchDescription(foundItem.href)
      }
    }
  }, [])

  useEffect(() => {
    const currentUrl = window.location.href
    const hasCampaignParam =
      currentUrl.includes('?campanha001') || currentUrl.includes('&campanha001')

    if (hasCampaignParam) {
      setIsCampaignMode(true)
    } else {
      setIsCampaignMode(false)
    }
  }, [location, lang])

  function redirectLogin() {
    window.location.href = 'https://www.procob.com/comercialprocob/'
  }

  return (
    <MaterialProvider theme={ThemeMaterial}>
      <ThemeProvider theme={ThemeStyled}>
        <AppContainer id="appContainer">
          <noscript>
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=GTM-MR53L2CH"
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            ></iframe>
          </noscript>
          <Header />
          <StyledContainer id="mainContainer">
            {minMedia1250 && <SidebarLeft />}
            <Main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/obrigado" element={<Thanks />} />
                <Route path="/*" element={<Docs />} />
              </Routes>
            </Main>

            {isCampaignMode && maxMedia1250 && (
              <StyledButtonCampaign>
                <ButtonContained
                  onClick={redirectLogin}
                  size="lg"
                  text={lang.header.contact}
                />
              </StyledButtonCampaign>
            )}
            {location.pathname !== '/' && location.pathname !== '/obrigado' && (
              <SidebarRight />
            )}
          </StyledContainer>
        </AppContainer>
      </ThemeProvider>
    </MaterialProvider>
  )
}
